// Route configuration for MCMS application

// Import auth components
import Login from "../screens/auth/Login";
import ForgotPassword from "../screens/auth/ForgotPassword.jsx";
import ResetPassword from "../screens/auth/ResetPassword.jsx";

// Import all components
import DashboardNew from "../screens/DashboardNew.jsx";
import Patients from "../screens/patients/Patients";
import PatientVisits from "../screens/patientVisits/PatientVisits";
import Visits from "../screens/visits/Visits";
import Logistics from "../screens/logistics/Logistics";
import Invoices from "../screens/invoices/Invoices";
import Reports from "../screens/Reports";
import Users from "../screens/admin/Users.jsx";
import Camps from "../screens/organization/OrganizationCampList.jsx";

import Organizations from "../screens/organization/OrganizationList.jsx";
import Profile from "../screens/profile/UserProfile.jsx";
import CampUserList from "../screens/camp/CampUserList.jsx";
import CampList from "../screens/camp/CampList.jsx";

import Donor from "../screens/admin/Donor.jsx";
import DonationList from "../screens/admin/DonationList.jsx";

// Import reports
import PreTreatmentReport from "../screens/reports/PreTreatmentReports.jsx";
import PostTreatmentReport from "../screens/reports/PostTreatmentReports.jsx";
import CampManagerReport from "../screens/reports/campManager/CampManager.jsx";
import DonorReportList from "../screens/reports/DonorReport.jsx";
import DoctorReportList from "../screens/reports/DoctorReport.jsx";
import DoctorTreatmentReport from "../screens/reports/DoctorTreatment.jsx";
import TenantReportList from "../screens/reports/TenantReport.jsx";
import TenantPatientVisitReport from "../screens/reports/TenantPatientVisitReport";

// Import notifications
import SendNotification from "../screens/admin/notifications/SendNotification.jsx";
import ManageNotification from "../screens/admin/notifications/ManageNotification";
import NotificationHistory from "../screens/admin/notifications/NotificationHistory";
import NewTemplate from "../screens/admin/notifications/NewTemplate";
import ViewNotification from "../screens/admin/notifications/ViewNotification";

// Import print pages
import DischargeSummary from '../components/features/printPage/DischargeSummary.jsx';
import InvoicePrint from '../components/features/printPage/InvoicePrint.jsx';

// Import utilities
import { Feature } from "../utils/utils.js";
import { PERMISSIONS } from "./permissions.js";
import SystemSettings from "../screens/admin/SystemSettings";
import RolesAndPermissionPage from "../screens/admin/RolesAndPermissionPage.jsx";

/**
 * Generate application routes based on user permissions
 * @param {Array} assignedPermission - Array of permission IDs for the current user
 * @returns {Array} Array of route objects
 */
export const generateRoutes = (assignedPermission = []) => {
  return [
    {
      path: "/dashboard",
      element: <DashboardNew />,
      feature: assignedPermission?.includes(PERMISSIONS.DASHBOARD_PAGE_VIEW),
    },
    {
      path: "/patients",
      element: <Patients />,
      feature: assignedPermission?.includes(PERMISSIONS.PATIENTS_PAGE_VIEW),
    },
    {
      path: "/patientVisits",
      element: <PatientVisits />,
      feature: assignedPermission?.includes(
        PERMISSIONS.PATIENT_VISIT_PAGE_VIEW
      ),
    },
    {
      path: "/logistics",
      element: <Logistics />,
      feature: assignedPermission?.includes(PERMISSIONS.LOGISTICS_PAGE_VIEW),
    },
    {
      path: "/invoices",
      element: <Invoices />,
      feature: assignedPermission?.includes(PERMISSIONS.INVOICES_PAGE_VIEW),
    },
    {
      path: "/reports",
      element: <Reports />,
      feature: assignedPermission?.includes(PERMISSIONS.REPORT_PAGE_VIEW),
    },

    {
      path: "/patient/:id",
      element: <Visits />,
      feature: assignedPermission?.includes(
        PERMISSIONS.PATIENT_VISIT_PAGE_VIEW
      ),
    },
    {
      path: "/users",
      element: <Users />,
      feature: assignedPermission?.includes(PERMISSIONS.USER_PAGE_VIEW),
    },
    {
      path: "/organizations/:organization_id",
      element: <Camps />,
      feature: assignedPermission?.includes(PERMISSIONS.ORGANIZATION_PAGE_VIEW),
    },
    {
      path: "/organizations",
      element: <Organizations />,
      feature: assignedPermission?.includes(PERMISSIONS.ORGANIZATION_PAGE_VIEW),
    },
    {
      path: "/roles",
      element: <RolesAndPermissionPage />,
      feature: assignedPermission?.includes(
        PERMISSIONS.ROLES_AND_PERMISSION_PAGE_VIEW
      ),
    },
    {
      path: "/notifications/send",
      element: <SendNotification />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_SEND_PAGE_VIEW
      ),
    },
    {
      path: "/notifications/manage",
      element: <ManageNotification />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_TEMPLATE_PAGE_VIEW
      ),
    },
    {
      path: "/notifications/history",
      element: <NotificationHistory />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_HISTORY_PAGE_VIEW
      ),
    },
    {
      path: "/notifications/new-template",
      element: <NewTemplate />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_TEMPLATE_PAGE_VIEW
      ),
    },
    {
      path: "/notifications/view",
      element: <ViewNotification />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_HISTORY_PAGE_VIEW
      ),
    },
    {
      path: "/systemsettings",
      element: <SystemSettings />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_HISTORY_PAGE_VIEW
      ),
    },
    
    {
      path: "/profile",
      element: <Profile />,
      feature: Feature.listOfFeature.basicView,
    },
    {
      path: "/camp/:id",
      element: <CampUserList />,
      feature: assignedPermission?.includes(PERMISSIONS.CAMP_PAGE_VIEW),
    },
    {
      path: "/camp",
      element: <CampList />,
      feature: assignedPermission?.includes(PERMISSIONS.CAMP_PAGE_VIEW),
    },
    {
      path: "/donors",
      element: <Donor />,
      feature: assignedPermission?.includes(PERMISSIONS.DONOR_PAGE_VIEW),
    },
    {
      path: "/donor/:id",
      element: <DonationList />,
      feature: assignedPermission?.includes(PERMISSIONS.DONOR_PAGE_VIEW),
    },
    {
      path: "reports/donor",
      element: <DonorReportList />,
      feature: assignedPermission?.includes(PERMISSIONS.DONOR_REPORT_PAGE_VIEW),
    },
    {
      path: "reports/doctor",
      element: <DoctorReportList />,
      feature: assignedPermission?.includes(
        PERMISSIONS.DOCTOR_REPORT_PAGE_VIEW
      ),
    },
    {
      path: "reports/doctor/treatment/:id",
      element: <DoctorTreatmentReport />,
      feature: assignedPermission?.includes(
        PERMISSIONS.DOCTOR_REPORT_PAGE_VIEW
      ),
    },
    {
      path: "reports/campManager",
      element: <CampManagerReport />,
      feature: assignedPermission?.includes(
        PERMISSIONS.CAMP_MANAGER_REPORT_PAGE_VIEW
      ),
    },
    {
      path: "reports/tenant",
      element: <TenantReportList />,
      feature: assignedPermission?.includes(
        PERMISSIONS.TENANT_REPORT_PAGE_VIEW
      ),
    },
    {
      path: "reports/tenant/patient/:id",
      element: <TenantPatientVisitReport />,
      feature: assignedPermission?.includes(
        PERMISSIONS.TENANT_REPORT_PAGE_VIEW
      ),
    },
    {
      path: "/settings/notifications/send",
      element: <SendNotification />,
      feature: assignedPermission?.includes(
        PERMISSIONS.SEND_NOTIFICATION_PAGE_VIEW
      ),
    },
    {
      path: "/settings/notifications/manage",
      element: <ManageNotification />,
      feature: assignedPermission?.includes(
        PERMISSIONS.MANAGE_NOTIFICATION_PAGE_VIEW
      ),
    },
    {
      path: "/settings/notifications/history",
      element: <NotificationHistory />,
      feature: assignedPermission?.includes(
        PERMISSIONS.NOTIFICATION_HISTORY_PAGE_VIEW
      ),
    },
  ];
};

// Auth routes (redirect to dashboard if authenticated)
export const authRoutes = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/forgotpassword",
    element: <ForgotPassword />,
  },
  {
    path: "/resetpassword",
    element: <ResetPassword />,
  },
];

// Public routes (no authentication required)
export const publicRoutes = [
  {
    path: "/dischargesummary",
    element: <DischargeSummary />,
  },
  {
    path: "/pretreatmentreport",
    element: <PreTreatmentReport />,
  },
  {
    path: "/posttreatmentreport",
    element: <PostTreatmentReport />,
  },
  {
    path: "/print",
    element: <InvoicePrint />,
  },
];
